<template>
  <div class="order-item-form">
    <Form ref="orderItemForm" :model="formData" label-position="top">
      <FormItem prop="referenced_type"
                :label="$t('quote.item_type_referenced_type')"
                :rules="{ required: true }">
        <Select v-model="formData.referenced_type" :disabled="disabledReferencedType">
          <Option value="product" :label="$t('quote.item_type_ref_product')"></Option>
          <Option value="manufacturer_part" :label="$t('quote.item_type_ref_manufacturer_part')"></Option>
          <Option value="fee_type" :label="$t('quote.item_type_ref_fee')"></Option>
        </Select>
      </FormItem>
      <FormItem :label="$t('order.product')" prop="referenced_id" v-if="formData.referenced_type === 'product'" required>
        <ProductFinder v-bind:product.sync="formData.referenced" v-model="formData.referenced_id" @selected="handleProductSelected"></ProductFinder>
      </FormItem>
      <div v-if="isManufacturerPartType">
        <FormItem :label="$t('order.manufacturer_part')" prop="manufacturer_part_id" :rules="{ required: true, message: $t('order.manufacturer_part_is_required') }">
          <Select v-if="selectProductManufacturerPart && formData.referenced_type === 'product'"
                  v-model="formData.manufacturer_part_id"
                  v-bind:manufacturer-part.sync="formData.manufacturer_part"
                  @on-change="handleProductManufacuturerPartSelected"
                  :disabled="productHasOneManufacturerPart">
            <Option v-for="mp in productManufacturerParts"
                    :key="mp.id" :value="mp.id"
                    :label="mp.display_name"></Option>
          </Select>
          <ManufacturerPartPicker v-else ref="ManufacturerPartPicker"
                                  v-model="formData.manufacturer_part_id"
                                  v-bind:manufacturer-part.sync="formData.manufacturer_part"
                                  :disabled="isManufacturerPartAssociated"
                                  :label="false">
          </ManufacturerPartPicker>
        </FormItem>
        <FormItem v-if="formData.is_manufactured"
                  prop="bom_version"
                  :label="$t('order.bom_version')"
                  :rules="{ required: formData.is_manufactured, message: $t('order.bom_version_is_required') }">
          <BomVersionsSelectByPart v-model="formData.bom_version"
                                  :version-number="formData.bom_version"
                                  :manufacturer-part-id="formData.manufacturer_part_id">
          </BomVersionsSelectByPart>
        </FormItem>
        <FormItem>
          <Checkbox v-model="formData.is_manufactured">{{ $t('order.is_manufactured') }}</Checkbox>
        </FormItem>
      </div>
      <FormItem v-if="formData.referenced_type === 'fee_type'" :prop="formData.referenced_id" :label="$t('order.fee_type')" required>
        <FeeTypeSelect v-model="formData.referenced_id" v-bind:fee.sync="formData.referenced" :width="840"></FeeTypeSelect>
      </FormItem>
      <Row :gutter="32">
        <Col span="12">
          <FormItem :label="$t('order.unit_price')" prop="unit_price" required>
            <Money v-model="formData.unit_price" :currency="formData.currency" disable-currency></Money>
            <div class="desc text-align-right" v-if="orderType === 'order_data_type__export'">{{ $t('quote.enter_price_with_tax_desc') }}</div>
          </FormItem>
        </Col>
        <Col span="12">
          <FormItem :label="$t('order.quantity')" prop="quantity" required>
            <InputNumber v-model=formData.quantity></InputNumber>
          </FormItem>
        </Col>
      </Row>
      <Row :gutter="32">
        <Col span="12">
          <FormItem :label="$t('order.unit_cost')" prop="unit_cost" required>
            <Money v-model="formData.unit_cost" :currency="formData.currency" disable-currency></Money>
            <div class="desc text-align-right" v-if="orderType === 'order_data_type__export'">{{ $t('quote.enter_price_with_tax_desc') }}</div>
          </FormItem>
        </Col>
        <Col span="12">
          <FormItem :label="$t('order.margin')" prop="margin">
            <Money v-model="formData.margin" :currency="formData.currency" disabled></Money>
          </FormItem>
        </Col>
      </Row>
      <Row :gutter="32" v-show="orderType != 'order_data_type__export'">
        <Col span="12">
          <FormItem prop="price_has_tax" :rules="{ required: true, message: 'Price has tax is required.' }" required>
            <Checkbox v-model="formData.price_has_tax" style="line-height: 106px;">{{ $t('order.is_tax_included') }}</Checkbox>
          </FormItem>
        </Col>
        <Col span="12">
          <FormItem :label="$t('order.tax_rate')" prop="tax_rate">
            <PercentInputNumber v-model="formData.tax_rate"></PercentInputNumber>
          </FormItem>
        </Col>
      </Row>
      <Row :gutter="32">
        <Col span="12">
          <FormItem :label="$t('order.delivery_date')" prop="delivery_date" :rules="{ required: true, message: $t('order.delivery_date_is_required') }">
            <DatePicker v-model="formData.delivery_date" type="date" format="yyyy-MM-dd"></DatePicker>
          </FormItem>
        </Col>
      </Row>
      <Row :gutter="32">
        <Col span="24">
          <FormItem :label="$t('order.item_remark')" prop="description" :rules="{ required: formData.referenced_type === 'fee_type', message: $t('order.item_remark_is_required') }">
            <Input v-model="formData.description"></Input>
          </FormItem>
        </Col>
      </Row>
      <FormItem class="text-align-right">
        <Button type="primary" @click="handleSave" :loading="saving">{{ $t('save') }}</Button>
      </FormItem>
    </Form>
  </div>
</template>

<script>
import { ProductFinder } from '@/view/product/components'
import { ManufacturerPartPicker } from '@/view/part/components'
import { FeeTypeSelect } from '@/view/quote/components/form-controls'
import { BomVersionsSelectByPart } from '@/view/bom/versions'
import { Money } from '@/components/inputs'
import { PercentInputNumber } from '@/components/nexpcb'
import { mapActions } from 'vuex'
import moment from 'moment'
export default {
  name: 'OrderItemForm',
  components: {
    ProductFinder,
    Money,
    ManufacturerPartPicker,
    BomVersionsSelectByPart,
    PercentInputNumber,
    FeeTypeSelect
  },
  props: {
    orderId: Number,
    orderType: String,
    currency: String,
    afterSaved: Function
  },
  computed: {
    isManufacturerPartType () {
      return (this.formData.referenced_type === 'product' &&
              this.formData.referenced &&
              this.formData.referenced.product_type !== 'service') ||
              this.formData.referenced_type === 'manufacturer_part'
    },
    isManufacturerPartAssociated () {
      return !!(this.formData.referenced && this.formData.referenced.manufacturer_parts && this.formData.referenced.manufacturer_parts.length > 0)
    },
    isAssembledPart () {
      return this.formData.manufacturer_part &&
             this.formData.manufacturer_part.component_type === 'assembled_part'
    },
    disabledReferencedType () {
      return this.formData.id && this.formData.referenced_type !== ''
    }
  },
  data () {
    return {
      formData: {
        is_manufactured: false,
        manufacturer_part: null,
        product: null,
        currency: this.currency,
        price_has_tax: false,
        quantity: 1,
        tax_rate: 0.13
      },
      saving: false,
      selectProductManufacturerPart: false,
      productHasOneManufacturerPart: false,
      productManufacturerParts: []
    }
  },
  methods: {
    ...mapActions(['updateOrderItem', 'addOrderItem', 'getProduct', 'getProductReferencePluginData']),
    setData (data) {
      data.tax_rate = 0.13
      if (this.orderType === 'order_data_type__export') {
        data.price_has_tax = false
      } else {
        data.price_has_tax = true
      }

      this.formData = data
      if (data.referenced_type === 'product') {
        this.handleProductSelected(data.referenced)
      }

      if (data.manufacturer_part && data.manufacturer_part.component_type === "assembled_part") {
        this.$set(this.formData, 'bom_version', data.bom_version ? data.bom_version : null)
      }
      this.$refs['orderItemForm'].resetFields()
    },
    handleProductSelected (product) {
      if (product) {
        this.getProductReferencePluginData({ productId: product.id, plugin: "manufacturerPart" }).then((res) => {
          this.productManufacturerParts = []
          if (res.data && res.data.manufacturer_parts.length > 0) {
            if (res.data.manufacturer_parts.length === 1) {
              this.$set(this.formData, 'manufacturer_part_id', res.data ? res.data.manufacturer_parts[0].id : null)
              this.$set(this.formData, 'manufacturer_part', res.data ? res.data.manufacturer_parts[0] : null)
              this.selectProductManufacturerPart = true
              this.productHasOneManufacturerPart = true
              this.productManufacturerParts = res.data.manufacturer_parts
            } else {
              this.productHasOneManufacturerPart = false
              this.selectProductManufacturerPart = true
              this.productManufacturerParts = res.data.manufacturer_parts
            }
          }
        })
      }
    },
    handleProductManufacuturerPartSelected(manufacturer_part_id) {
      this.formData.manufacturer_part = this.productManufacturerParts.find(pmp => pmp.id === manufacturer_part_id)
      this.formData.manufacturer_part_id = manufacturer_part_id
      return this.isAssembledPart
    },
    handleSave () {
      this.$refs['orderItemForm'].validate((valid) => {
        if (valid) {
          // fix date
          this.formData.delivery_date = moment(this.formData.delivery_date).format('YYYY-MM-DD')
          if (!this.formData.referenced_id && this.formData.referenced_type === 'manufacturer_part') {
            this.formData.referenced_id = this.formData.manufacturer_part_id
          }

          // to be compatable with the previous version without adding referenced field,
          // this will be removed when product_id property being removed from order item table.
          if (this.formData.referenced_type === 'product') {
            this.formData.product_id = this.formData.referenced_id
          }
          this.saving = true
          if (this.formData.id) {
            this.updateOrderItem({ orderItemId: this.formData.id, attributes: this.formData })
              .then(() => {
                this.afterSaved()
              })
              .finally(() => { this.saving = false })
          } else {
            this.addOrderItem({ orderId: this.orderId, attributes: this.formData })
              .then(() => {
                this.afterSaved()
              })
              .finally(() => { this.saving = false })
          }
        } else {
          this.$Message.error('The form has error, cannot submit!')
        }
      })
    }
  }
}
</script>
